import {
  AddGroupMembershipRequest,
  RemoveGroupMembershipRequest,
  FetchGroupMembershipsRequest,
  FetchGroupMembershipsResponse,
  FetchGroupsRequest,
  FetchGroupsResponse,
  FetchGroupsTreeRequest,
  FetchGroupsTreeResponse,
  groupsService,
  UpdateGroupMembershipRequest,
} from '@aslanpay/portal-common-lib';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const useFetchGroups = (
  {
    companyId,
    groupId,
    individualId,
    isPrimary,
    provisionMode,
    archived,
    parentGroupId,
    name,
    sortBy,
    sort,
    page,
    size,
  }: FetchGroupsRequest,
  enabled = true
) => {
  return useQuery<FetchGroupsResponse, Error>({
    queryKey: [
      'fetchGroups',
      {
        companyId,
        groupId,
        individualId,
        isPrimary,
        provisionMode,
        archived,
        parentGroupId,
        name,
        sortBy,
        sort,
        page,
        size,
      },
    ],
    queryFn: () => {
      if (!companyId) throw new Error('companyId is required');
      return groupsService.fetchGroups({
        companyId,
        groupId,
        individualId,
        isPrimary,
        provisionMode,
        archived,
        parentGroupId,
        name,
        sortBy,
        sort,
        page,
        size,
      });
    },
    enabled: enabled && !!companyId,
  });
};

const useFetchGroupsTree = (
  companyId: string | undefined,
  params?: FetchGroupsTreeRequest,
  enabled = true
) => {
  return useQuery<FetchGroupsTreeResponse, Error>({
    queryKey: ['fetchGroupsTree', { companyId, ...params }],
    queryFn: () => {
      if (!companyId) throw new Error('companyId is required');
      return groupsService.fetchGroupsTree(companyId, params);
    },
    enabled: enabled && !!companyId,
  });
};

// Fetch all group memberships for a company in a single request
const useFetchCompanyGroupMemberships = (companyId: string | undefined, enabled = true) => {
  return useQuery<FetchGroupMembershipsResponse, Error>({
    queryKey: ['fetchCompanyGroupMemberships', { companyId }],
    queryFn: () => {
      if (!companyId) throw new Error('companyId is required');
      return groupsService.fetchAllGroupMemberships(companyId, {
        status: 'ACTIVE',
        page: 0,
        size: 999999,
      });
    },
    enabled: enabled && !!companyId,
  });
};

const useFetchPotManagers = (
  companyId: string | undefined,
  params?: { query?: string; page?: number; size?: number },
  enabled = true
) => {
  return useQuery<FetchGroupMembershipsResponse, Error>({
    queryKey: ['fetchPotManagers', { companyId, ...params }],
    queryFn: () => {
      if (!companyId) throw new Error('companyId is required');
      return groupsService.fetchAllGroupMemberships(companyId, {
        membershipRole: 'POT_MANAGER',
        status: 'ACTIVE',
        query: params?.query,
        page: params?.page ?? 0,
        size: params?.size ?? 20,
      });
    },
    enabled: enabled && !!companyId,
  });
};

const useFetchGroupMemberships = (
  companyId: string | undefined,
  groupId: string | undefined,
  params?: Omit<FetchGroupMembershipsRequest, 'page' | 'size'>,
  enabled = true
) => {
  return useInfiniteQuery<FetchGroupMembershipsResponse, Error>({
    queryKey: ['fetchGroupMemberships', { companyId, groupId, ...params }],
    queryFn: ({ pageParam = 0 }) => {
      if (!companyId) throw new Error('companyId is required');
      if (!groupId) throw new Error('groupId is required');

      return groupsService.fetchGroupMemberships(companyId, groupId, {
        ...params,
        page: pageParam as number,
        size: 999,
      });
    },
    getNextPageParam: (lastPage) => {
      const fetched = (lastPage.pageNumber + 1) * lastPage.pageSize;
      return fetched < lastPage.total ? lastPage.pageNumber + 1 : undefined;
    },
    enabled: enabled && !!companyId && !!groupId,
  });
};

const useUpdateGroupMemberships = (companyId: string | undefined, groupId: string | undefined) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: UpdateGroupMembershipRequest) => {
      if (!companyId) throw new Error('companyId is required');
      if (!groupId) throw new Error('groupId is required');

      return groupsService.updateGroupMemberships(companyId, groupId, body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['fetchGroupMemberships', { companyId, groupId }],
      });
      queryClient.invalidateQueries({
        queryKey: ['fetchGroups', { companyId }],
      });
      queryClient.invalidateQueries({
        queryKey: ['fetchGroupsTree', { companyId }],
      });
    },
  });
};

const useAddGroupMemberships = (companyId: string | undefined, groupId: string | undefined) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: AddGroupMembershipRequest) => {
      if (!companyId) throw new Error('companyId is required');
      if (!groupId) throw new Error('groupId is required');

      return groupsService.addGroupMemberships(companyId, groupId, body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['fetchGroupMemberships', { companyId, groupId }],
      });
      queryClient.invalidateQueries({
        queryKey: ['fetchGroups', { companyId }],
      });
      queryClient.invalidateQueries({
        queryKey: ['fetchGroupsTree', { companyId }],
      });
    },
  });
};

const useRemoveGroupMemberships = (companyId: string | undefined, groupId: string | undefined) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: RemoveGroupMembershipRequest) => {
      if (!companyId) throw new Error('companyId is required');
      if (!groupId) throw new Error('groupId is required');

      return groupsService.removeGroupMemberships(companyId, groupId, body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['fetchGroupMemberships', { companyId, groupId }],
      });
      queryClient.invalidateQueries({
        queryKey: ['fetchGroups', { companyId }],
      });
      queryClient.invalidateQueries({
        queryKey: ['fetchGroupsTree', { companyId }],
      });
    },
  });
};

const useGroups = {
  useFetchGroups,
  useFetchGroupsTree,
  useFetchGroupMemberships,
  useFetchCompanyGroupMemberships,
  useFetchPotManagers,
  useAddGroupMemberships,
  useRemoveGroupMemberships,
  useUpdateGroupMemberships,
};

export default useGroups;
